import { useEffect, useState } from 'react';
import { track } from '@amplitude/analytics-browser';
import { documentsService } from 'commons/services';
import { pdfThumbnailPath, useTranslationWithPrefix } from 'commons/utils';
import { logFailure, logFileSize } from 'commons/utils/documentUploader/logger';
import { config } from 'config';
import useValidateFile from './useValidateFile';

interface UseDocumentUploaderProps {
  documentName: string;
  onSuccess?: () => void;
  onError: (text: string) => void;
  onDelete: () => void;
  onFileSelect?: (file: File) => void;
  showPreview?: boolean;
  autoUpload?: boolean;
  initialThumbnailFile?: string;
  maxDocumentSize?: number;
  errorEventName?: string;
}

export function useDocumentUploader({
  documentName,
  onSuccess,
  onError,
  onDelete,
  onFileSelect,
  showPreview = true,
  autoUpload = false,
  initialThumbnailFile = '',
  maxDocumentSize = config.documents.maxSize,
  errorEventName,
}: UseDocumentUploaderProps) {
  const { t } = useTranslationWithPrefix('commons.documentUploader');

  const [thumbnailImageSrc, setThumbnailImageSrc] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const { validateFile } = useValidateFile();

  useEffect(() => {
    const fetchFileType = async () => {
      if (!initialThumbnailFile) return;
      const file = await fetch(initialThumbnailFile);
      const blob = await file.blob();
      if (blob.type === 'application/pdf') {
        setThumbnailImageSrc(pdfThumbnailPath);
      } else {
        setThumbnailImageSrc(initialThumbnailFile);
      }
    };
    fetchFileType();
  }, [initialThumbnailFile]);

  const trackDocumentError = (errorType: 'validation' | 'server') => {
    if (!errorEventName) return;
    track(errorEventName, {
      document_name: documentName,
      error_type: errorType,
    });
  };

  const handleFileChange = async (file: File) => {
    const validation = validateFile(file, maxDocumentSize);
    if (!validation.isValid) {
      trackDocumentError('validation');
      onError(validation.errorMessage!);
      return;
    }

    logFileSize(file, documentName);
    onError('');

    setFileName(file.name);
    if (onFileSelect) {
      onFileSelect(file);
    }

    if (autoUpload && !onFileSelect) {
      setIsLoading(true);
      try {
        const response = await documentsService.uploadDocuments(
          documentName,
          file,
        );
        if (file.type === 'application/pdf') {
          setThumbnailImageSrc('');
        } else {
          setThumbnailImageSrc(response.url);
        }
        onSuccess?.();
      } catch (error) {
        trackDocumentError('server');
        logFailure(file, documentName, error);
        onError(t('errors.serverError'));
        setFileName(null);
        setPreview(null);
      } finally {
        setIsLoading(false);
      }
    } else if (showPreview) {
      setIsLoading(true);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setPreview(ev.target?.result as string);
          setIsLoading(false);
        };
        reader.onerror = () => {
          setIsLoading(false);
          onError(t('errors.serverError'));
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
        setIsLoading(false);
      }
    }
  };

  const handleFileDelete = () => {
    setThumbnailImageSrc('');
    setPreview(null);
    setFileName(null);
    onDelete();
  };

  return {
    thumbnailImageSrc,
    isLoading,
    preview,
    fileName,
    handleFileChange,
    handleFileDelete,
  };
}
